import * as Yup from 'yup';
import { useFormik } from 'formik';
import Image from 'next/future/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import React, { Fragment, useEffect } from 'react';

import { getGuestOrder } from '@/fetchers/return';

import { useAlert } from '@/context/AlertProvider';
import { getToken, isLogged } from '@/helpers/auth';
import { getLangCountry } from '@/helpers/languages';

import MyHead from '@/components/MyHead';
import { Button } from '@/components/v2/UI/Button';
import Input from '@/components/v2/Forms/Input';

const ReturnPage: React.FC = () => {
  const alert = useAlert();
  const token = getToken();
  const router = useRouter();
  const { t } = useTranslation();
  const { lang, country } = getLangCountry(router.query);
  const logged = isLogged();

  const formik = useFormik({
    validateOnChange: false,
    initialValues: {
      email: '',
      id_order: ''
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
          t('INVALID_EMAIL')
        )
        .required(t('REQUIRED_FIELD')),
      id_order: Yup.string().required(t('REQUIRED_FIELD'))
    }),
    async onSubmit(values) {
      try {
        if (values.id_order[0] === '#') {
          values.id_order = values.id_order.slice(1);
        }

        const order = await getGuestOrder({ token, lang, country, ...values });

        if (order.return === 0) {
          return alert.error(t('RETURN_NOT_ELIGIBLE'));
        }

        formik.resetForm();

        router.push(
          `/${lang}_${country}/return/${order.id}?email=${values.email}`
        );
      } catch (error: any) {
        if (error.response.status === 404) {
          alert.error(t('WRONG_ORDER_OR_EMAIL'));
        } else {
          alert.error(t('GENERIC_ERROR'));
        }
      }
    }
  });

  useEffect(() => {
    if (logged && router.isReady) {
      router.replace(`/${lang}_${country}/profile/returns`);
    }
  }, [router.query]);

  return (
    <Fragment>
      <MyHead />

      <div className="section space-y-md">
        <h1 className="h2">{t('P_RETURNS.TITLE')}</h1>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="border space-y-6 px-6 py-8 md:px-8 xl:px-12">
            <p className="text-body">{t('P_RETURNS.FORM.DESC')}</p>

            <form className="space-y-6" onSubmit={formik.handleSubmit}>
              <div className="space-y-3">
                <Input
                  type="email"
                  name="email"
                  label={t('MAIL_ADDRESS')}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.errors.email}
                  required
                />

                <Input
                  type="text"
                  name="id_order"
                  label={t('ORDER_NUMBER')}
                  value={formik.values.id_order}
                  onChange={formik.handleChange}
                  error={formik.errors.id_order}
                  required
                />
              </div>

              <Button
                type="submit"
                variant="dark"
                loading={formik.isSubmitting}
                className="w-full"
              >
                {t('SEND_REQUEST')}
              </Button>
            </form>

            <div className="bg-sand-100 text-center text-sand-500 space-y-6 px-6 py-8 md:px-8 xl:px-12">
              <div className="h5">{t('P_RETURNS.RETURN_POLICY.TITLE')}</div>
              <p className="text-body">{t('P_RETURNS.RETURN_POLICY.DESC')}</p>
            </div>
          </div>

          <div className="border space-y-12 px-6 py-8 md:px-8 xl:px-12 bg-gray-200">
            <h2 className="h5 text-center">{t('P_RETURNS.HOW.TITLE')}</h2>

            <div className="flex justify-center">
              <Image
                src="/v2/return/return-box.png"
                alt="Return Box"
                width={0}
                height={0}
                sizes="100vw"
                className="w-24 h-auto"
              />
            </div>

            <ul className="text-center space-y-6">
              <li className="space-y-3">
                <div className="flex justify-center items-center h5 text-base space-x-2">
                  <div className="h-6 w-6 flex justify-center items-center border-2 border-black rounded-full text-sm font-medium">
                    1
                  </div>
                  <span>{t('P_RETURNS.HOW.BOX_ONE.TITLE')}</span>
                </div>
                <p className="text-body">{t('P_RETURNS.HOW.BOX_ONE.DESC')}</p>
              </li>

              <li className="space-y-3">
                <div className="flex justify-center items-center h5 text-base space-x-2">
                  <div className="h-6 w-6 flex justify-center items-center border-2 border-black rounded-full text-sm font-medium">
                    2
                  </div>
                  <span>{t('P_RETURNS.HOW.BOX_TWO.TITLE')}</span>
                </div>
                <p className="text-body">{t('P_RETURNS.HOW.BOX_TWO.DESC')}</p>
              </li>

              <li className="space-y-3">
                <div className="flex justify-center items-center h5 text-base space-x-2">
                  <div className="h-6 w-6 flex justify-center items-center border-2 border-black rounded-full text-sm font-medium">
                    3
                  </div>
                  <span>{t('P_RETURNS.HOW.BOX_THREE.TITLE')}</span>
                </div>
                <p className="text-body">{t('P_RETURNS.HOW.BOX_THREE.DESC')}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ReturnPage;
